
import { useState } from 'react';
import { useCrypto } from '../../hooks/useCrypto';
import Header from '../../components/feature/Header';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Card from '../../components/base/Card';

const Wallet = () => {
  const { cryptoData, loading, transactions, walletBalances, depositCrypto, withdrawCrypto, transferCrypto } = useCrypto();
  const [activeTab, setActiveTab] = useState<'overview' | 'deposit' | 'withdraw' | 'transfer'>('overview');
  const [selectedAsset, setSelectedAsset] = useState('bitcoin');
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const totalBalance = walletBalances.reduce((sum, balance) => sum + balance.value, 0);
  const totalChange = walletBalances.reduce((sum, balance) => sum + (balance.value * balance.change / 100), 0);
  const totalChangePercent = totalBalance > 0 ? (totalChange / totalBalance) * 100 : 0;

  const selectedCrypto = cryptoData.find(crypto => crypto.id === selectedAsset);
  const selectedBalance = walletBalances.find(balance => balance.asset === selectedAsset);

  const handleDeposit = async () => {
    if (!amount) {
      alert('Please enter an amount');
      return;
    }

    if (!selectedCrypto) {
      alert('Please select a valid asset');
      return;
    }

    setIsProcessing(true);
    
    try {
      const depositAddress = `1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa${Math.random().toString(36).substr(2, 8)}`;
      const success = depositCrypto(selectedCrypto.symbol, parseFloat(amount), depositAddress);
      
      if (success) {
        alert(`Deposit request submitted!\nAmount: ${amount} ${selectedCrypto.symbol.toUpperCase()}\nAddress: ${depositAddress}\n\nYour funds will be credited after network confirmation.`);
        setAmount('');
      } else {
        alert('Deposit request failed. Please try again.');
      }
    } catch (error) {
      alert('Deposit failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    if (!amount || !address) {
      alert('Please fill in all fields');
      return;
    }

    if (!selectedCrypto || !selectedBalance) {
      alert('Please select a valid asset');
      return;
    }

    const withdrawAmount = parseFloat(amount);
    if (withdrawAmount > selectedBalance.balance) {
      alert('Insufficient balance');
      return;
    }

    setIsProcessing(true);
    
    try {
      const success = withdrawCrypto(selectedCrypto.symbol, withdrawAmount, address);
      
      if (success) {
        alert(`Withdrawal request submitted!\nAmount: ${amount} ${selectedCrypto.symbol.toUpperCase()}\nTo: ${address}\n\nYour withdrawal is being processed.`);
        setAmount('');
        setAddress('');
      } else {
        alert('Withdrawal failed. Please check your balance and try again.');
      }
    } catch (error) {
      alert('Withdrawal failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTransfer = async () => {
    if (!amount || !address) {
      alert('Please fill in all fields');
      return;
    }

    if (!selectedCrypto || !selectedBalance) {
      alert('Please select a valid asset');
      return;
    }

    const transferAmount = parseFloat(amount);
    if (transferAmount > selectedBalance.balance) {
      alert('Insufficient balance');
      return;
    }

    setIsProcessing(true);
    
    try {
      const success = transferCrypto(selectedCrypto.symbol, transferAmount, address);
      
      if (success) {
        alert(`Transfer request submitted!\nAmount: ${amount} ${selectedCrypto.symbol.toUpperCase()}\nTo: ${address}\n\nYour transfer is being processed.`);
        setAmount('');
        setAddress('');
      } else {
        alert('Transfer failed. Please check your balance and try again.');
      }
    } catch (error) {
      alert('Transfer failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading wallet data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Wallet</h1>
          <p className="text-gray-600">Manage your cryptocurrency assets and transactions</p>
        </div>

        {/* Portfolio Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Total Balance</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-wallet-3-line text-2xl text-blue-500"></i>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold">${totalBalance.toLocaleString()}</p>
              <p className={`text-sm flex items-center ${
                totalChangePercent >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                <i className={`ri-arrow-${totalChangePercent >= 0 ? 'up' : 'down'}-line mr-1`}></i>
                {totalChangePercent >= 0 ? '+' : ''}${totalChange.toLocaleString()} ({totalChangePercent.toFixed(2)}%)
              </p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Active Assets</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-coins-line text-2xl text-green-500"></i>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold">{walletBalances.length}</p>
              <p className="text-sm text-gray-600">Different cryptocurrencies</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Total Transactions</h3>
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-bar-chart-line text-2xl text-purple-500"></i>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold">{transactions.length}</p>
              <p className="text-sm text-green-600 flex items-center">
                <i className="ri-arrow-up-line mr-1"></i>
                All time activity
              </p>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Asset Balances */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-6">Asset Balances</h3>
              <div className="space-y-4">
                {walletBalances.map((balance) => {
                  const coinData = cryptoData.find(coin => coin.id === balance.asset);
                  return (
                    <div key={balance.asset} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        {coinData && (
                          <img src={coinData.image} alt={coinData.name} className="w-10 h-10" />
                        )}
                        <div>
                          <p className="font-semibold">{balance.symbol}</p>
                          <p className="text-sm text-gray-600">{coinData?.name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{balance.balance.toFixed(6)} {balance.symbol}</p>
                        <p className="text-sm text-gray-600">${balance.value.toLocaleString()}</p>
                        <p className={`text-sm ${
                          balance.change >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {balance.change >= 0 ? '+' : ''}{balance.change.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Transaction History */}
            <Card className="p-6 mt-6">
              <h3 className="text-xl font-semibold mb-6">Recent Transactions</h3>
              {transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">Type</th>
                        <th className="text-left py-3">Asset</th>
                        <th className="text-left py-3">Amount</th>
                        <th className="text-left py-3">Status</th>
                        <th className="text-left py-3">Date</th>
                        <th className="text-left py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 10).map((tx) => (
                        <tr key={tx.id} className="border-b">
                          <td className="py-4">
                            <div className="flex items-center space-x-2">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
                                tx.type === 'deposit' || tx.type === 'buy' ? 'bg-green-100' :
                                tx.type === 'withdrawal' || tx.type === 'sell' ? 'bg-red-100' : 'bg-blue-100'
                              }`}>
                                <i className={`ri-${
                                  tx.type === 'deposit' ? 'arrow-down' :
                                  tx.type === 'withdrawal' ? 'arrow-up' :
                                  tx.type === 'buy' ? 'add' :
                                  tx.type === 'sell' ? 'subtract' :
                                  tx.type === 'convert' ? 'exchange' : 'send'
                                }-line text-sm ${
                                  tx.type === 'deposit' || tx.type === 'buy' ? 'text-green-600' :
                                  tx.type === 'withdrawal' || tx.type === 'sell' ? 'text-red-600' : 'text-blue-600'
                                }`}></i>
                              </div>
                              <span className="capitalize">{tx.type}</span>
                            </div>
                          </td>
                          <td className="py-4 font-medium">
                            {tx.type === 'convert' ? `${tx.fromAsset} → ${tx.toAsset}` : tx.asset}
                          </td>
                          <td className="py-4">
                            {tx.type === 'convert' ? 
                              `${tx.amount} → ${tx.toAmount?.toFixed(6)}` : 
                              tx.amount.toLocaleString()
                            }
                          </td>
                          <td className="py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              tx.status === 'completed' ? 'bg-green-100 text-green-800' :
                              tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {tx.status}
                            </span>
                          </td>
                          <td className="py-4 text-sm text-gray-600">
                            {new Date(tx.date).toLocaleString()}
                          </td>
                          <td className="py-4">
                            {tx.txHash && (
                              <button className="text-blue-600 hover:text-blue-800 text-sm whitespace-nowrap">
                                View Details
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="ri-history-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600">No transactions yet</p>
                  <p className="text-sm text-gray-500">Your transaction history will appear here</p>
                </div>
              )}
            </Card>
          </div>

          {/* Wallet Actions */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-6">Wallet Actions</h3>
              
              {/* Tab Navigation */}
              <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1 mb-6">
                {[
                  { key: 'overview', label: 'Overview', icon: 'ri-eye-line' },
                  { key: 'deposit', label: 'Deposit', icon: 'ri-arrow-down-line' },
                  { key: 'withdraw', label: 'Withdraw', icon: 'ri-arrow-up-line' },
                  { key: 'transfer', label: 'Transfer', icon: 'ri-exchange-line' },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab.key
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <i className={`${tab.icon} mr-1`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <div className="text-center py-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4">
                      <i className="ri-wallet-3-line text-2xl text-blue-600"></i>
                    </div>
                    <h4 className="text-lg font-semibold mb-2">Wallet Overview</h4>
                    <p className="text-gray-600 text-sm mb-4">
                      Manage your crypto assets with secure deposits, withdrawals, and transfers.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <Button
                        onClick={() => setActiveTab('deposit')}
                        className="bg-green-500 hover:bg-green-600 text-white whitespace-nowrap"
                      >
                        <i className="ri-arrow-down-line mr-2"></i>
                        Deposit
                      </Button>
                      <Button
                        onClick={() => setActiveTab('withdraw')}
                        className="bg-red-500 hover:bg-red-600 text-white whitespace-nowrap"
                      >
                        <i className="ri-arrow-up-line mr-2"></i>
                        Withdraw
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'deposit' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Asset
                    </label>
                    <select
                      value={selectedAsset}
                      onChange={(e) => setSelectedAsset(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                    >
                      {cryptoData.slice(0, 10).map((crypto) => (
                        <option key={crypto.id} value={crypto.id}>
                          {crypto.symbol.toUpperCase()} - {crypto.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount
                    </label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="text-sm"
                    />
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Deposit Address</h4>
                    <p className="text-sm text-blue-700 font-mono break-all">
                      1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa
                    </p>
                    <button className="text-blue-600 hover:text-blue-800 text-sm mt-2 whitespace-nowrap">
                      <i className="ri-file-copy-line mr-1"></i>
                      Copy Address
                    </button>
                  </div>

                  <Button
                    onClick={handleDeposit}
                    disabled={isProcessing}
                    className="w-full bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      'Submit Deposit Request'
                    )}
                  </Button>
                </div>
              )}

              {activeTab === 'withdraw' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Asset
                    </label>
                    <select
                      value={selectedAsset}
                      onChange={(e) => setSelectedAsset(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                    >
                      {walletBalances.map((balance) => (
                        <option key={balance.asset} value={balance.asset}>
                          {balance.symbol} - Available: {balance.balance.toFixed(6)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Withdrawal Address
                    </label>
                    <Input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter wallet address"
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount
                    </label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="text-sm"
                    />
                    {selectedBalance && (
                      <p className="text-xs text-gray-500 mt-1">
                        Max: {selectedBalance.balance.toFixed(6)} {selectedBalance.symbol}
                      </p>
                    )}
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <i className="ri-information-line mr-1"></i>
                      Network fee: 0.0005 {selectedCrypto?.symbol.toUpperCase()}
                    </p>
                  </div>

                  <Button
                    onClick={handleWithdraw}
                    disabled={isProcessing}
                    className="w-full bg-red-500 hover:bg-red-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      'Withdraw Funds'
                    )}
                  </Button>
                </div>
              )}

              {activeTab === 'transfer' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Asset
                    </label>
                    <select
                      value={selectedAsset}
                      onChange={(e) => setSelectedAsset(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                    >
                      {walletBalances.map((balance) => (
                        <option key={balance.asset} value={balance.asset}>
                          {balance.symbol} - Available: {balance.balance.toFixed(6)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Recipient Address
                    </label>
                    <Input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter recipient address"
                      className="text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount
                    </label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="text-sm"
                    />
                    {selectedBalance && (
                      <p className="text-xs text-gray-500 mt-1">
                        Max: {selectedBalance.balance.toFixed(6)} {selectedBalance.symbol}
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={handleTransfer}
                    disabled={isProcessing}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      'Transfer Funds'
                    )}
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wallet;